import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useMedia } from '@/hooks/useMedia';
import { FoxyMark } from './guide/FoxyMark';
import { useStage } from './guide/stage';
import './one-brain.css';

// Owner copy (2026-10-16). Each line is backed: demo tiers, briefs and next steps, follow-ups held
// for review, CRM push; insights and demand signals, objections in briefs, flagged flows, clustering.
// Composition (owner, 2026-10-16): one centred vertical axis with the mark on it, strict mirror
// symmetry left and right — a centred header, then a 1fr · 200px · 1fr diagram whose two columns
// are mirror images (same line count, line height, width and start), with one hairline each way
// from the circle and ember dots travelling outward at the same cadence.
const gtm = ['Live demos on the real product', 'Briefs and next steps', 'Follow-ups, held for review', 'CRM rows, automatically'];
const product = ['Recurring questions', 'Objections, verbatim', 'Flows that confuse people', 'The words customers use for features'];

export function OneBrainSection() {
  const grid = useRef<HTMLDivElement>(null);
  const circle = useRef<HTMLDivElement>(null);
  const leftCol = useRef<HTMLDivElement>(null);
  const rightCol = useRef<HTMLDivElement>(null);
  const desktop = useMedia('(min-width: 1000px)');
  const reduced = useMedia('(prefers-reduced-motion: reduce)');
  const [wires, setWires] = useState<{ left: string; right: string; w: number; h: number } | null>(null);
  const stage = useStage();
  // The connector dots (2.4s loop, both sides at once): the plain mark thinks while dots flow in and
  // speaks (glow) while they flow out. B15: Mr. Clippy looks toward the flowing side; at the H2 a
  // 450ms wide look with a tiny lean back.
  const [markState, setMarkState] = useState<'listening' | 'thinking' | 'speaking'>('listening');
  const markLevel = useRef(0.3);
  // the loop runs only while the section is ≥ 40% visible: nothing animates off-screen
  const [active, setActive] = useState(false);
  useEffect(() => {
    const el = grid.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting && e.intersectionRatio >= 0.4), { threshold: [0, 0.4] });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!desktop || reduced || !active) { setMarkState('listening'); return; }
    let phase = 0;
    const id = window.setInterval(() => {
      phase = (phase + 1) % 4;
      setMarkState(phase < 2 ? 'thinking' : 'speaking');
      stage?.react('.fx-ob', 'clippy', { lookAt: phase % 2 === 0 ? leftCol.current : rightCol.current });
    }, 600);
    return () => window.clearInterval(id);
  }, [active, desktop, reduced, stage]);
  const h2 = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!stage) return;
    const el = h2.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) stage.react('.fx-ob', 'clippy', { eyes: { mode: 'wide', target: { x: -1, y: -0.3 }, key: 'ob-h2' }, leanBack: true }); }, { threshold: 1 });
    io.observe(el);
    return () => io.disconnect();
  }, [stage]);

  // Two hairlines at the circle's vertical centre: left edge → the GTM column's inner edge, and its
  // mirror. The right path is the left path mirrored about the axis.
  useLayoutEffect(() => {
    const el = grid.current;
    if (!el || !desktop) {
      setWires(null);
      return;
    }
    const measure = () => {
      const g = el.getBoundingClientRect();
      const c = circle.current?.getBoundingClientRect();
      const l = leftCol.current?.getBoundingClientRect();
      if (!c || !l) return;
      const axis = g.width / 2;
      const cy = c.top - g.top + c.height / 2;
      const x1 = c.left - g.left;
      const x2 = l.right - g.left;
      const left = `M ${x1} ${cy} L ${x2} ${cy}`;
      const right = `M ${2 * axis - x1} ${cy} L ${2 * axis - x2} ${cy}`;
      setWires({ left, right, w: g.width, h: g.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [desktop]);

  return (
    <section className="fx-ob fx-rv" id="one-brain">
      <div className="container">
        <div className="fx-ob-head">
          <p className="fx-eyebrow">One brain</p>
          <h2 ref={h2}>
            One brain.
            <br />
            Product and GTM share it.
          </h2>
          <p className="fx-ob-sub">
            Not two tools. One understanding of your product, used by everyone
            who sells it and everyone who builds it.
          </p>
        </div>
        <div className="fx-ob-grid" ref={grid}>
          {wires && (
            <svg className="fx-ob-wires" width={wires.w} height={wires.h} viewBox={`0 0 ${wires.w} ${wires.h}`} aria-hidden="true">
              <path id="fx-ob-left" d={wires.left} />
              <path id="fx-ob-right" d={wires.right} />
              {!reduced && active && (
                <>
                  <circle r="2.5" className="fx-ob-dot">
                    <animateMotion dur="2.4s" repeatCount="indefinite">
                      <mpath href="#fx-ob-left" />
                    </animateMotion>
                  </circle>
                  <circle r="2.5" className="fx-ob-dot">
                    <animateMotion dur="2.4s" repeatCount="indefinite">
                      <mpath href="#fx-ob-right" />
                    </animateMotion>
                  </circle>
                </>
              )}
            </svg>
          )}
          <div className="fx-ob-col gtm" ref={leftCol}>
            <span className="fx-ob-label">GTM gets</span>
            <ul>
              {gtm.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <div className="fx-ob-centre" ref={circle}>
            <FoxyMark size={64} state={markState} levelRef={markLevel} />
          </div>
          <div className="fx-ob-col product" ref={rightCol}>
            <span className="fx-ob-label">Product gets</span>
            <ul>
              {product.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
